import React from "react";
import { Link } from "react-router-dom";
import LeadForm from "@/components/LeadForm";
import usePageTitle from "@/hooks/usePageTitle";
import {
    ArrowRight, Phone, Zap, Banknote, Diamond, Check, Target,
    FileText, Briefcase, DollarSign, CreditCard, TrendingUp, ShieldCheck,
    Clock, Stethoscope, Wrench, Wrench as Tool, UtensilsCrossed, ShoppingBag, Sun,
    Star,
} from "lucide-react";

const stats = [
    { label: "Funded to date", value: "$420M+" },
    { label: "Businesses funded", value: "8,400+" },
    { label: "Avg. time to fund", value: "24 hrs" },
    { label: "Approval rate", value: "82%" },
];

const products = [
    { icon: Banknote, title: "Working Capital", amount: "$10K – $1M", desc: "Cash flow, payroll, inventory — funds in your account in 24 hours." },
    { icon: Briefcase, title: "Merchant Cash Advance", amount: "Up to $3M", desc: "Revenue-based capital tied to your future receivables. No fixed payments." },
    { icon: FileText, title: "Term Loans", amount: "$50K – $2M", desc: "Predictable monthly payments for major investments and expansion." },
    { icon: CreditCard, title: "Line of Credit", amount: "Up to $500K", desc: "Pre-approved capital. Draw what you need, pay only for what you use." },
    { icon: DollarSign, title: "SBA Loans", amount: "Up to $5M", desc: "Government-backed, low-rate, long-term capital for established businesses." },
    { icon: ShieldCheck, title: "Equipment Financing", amount: "Up to $2M", desc: "Trucks, machinery, kitchen, medical — finance up to 100% of the cost." },
];

const merchantPerks = [
    { icon: Zap, title: "24-Hour Funding", desc: "Most approved deals fund the next business day. Some same-day." },
    { icon: Phone, title: "Real Humans", desc: "A funding specialist handles your file from application to wire." },
    { icon: ShieldCheck, title: "No Hidden Fees", desc: "Transparent terms. What you see in the offer is what you sign." },
];

const whyMerchants = [
    { title: "Bad Credit? We Look at Revenue.", desc: "If your business is producing — even with 500-credit ownership — we have programs that fit." },
    { title: "Decisions in 4 Hours", desc: "Submit four months of bank statements and a one-page app. Real underwriters, real answers." },
    { title: "No Collateral on Most Programs", desc: "Up to $250K unsecured. Higher amounts available with light documentation." },
    { title: "Renew & Save", desc: "Pay early and we cut the cost. Top customers earn aggressive early-payment discounts." },
];

const steps = [
    { n: "01", title: "Apply", desc: "60-second online form. No credit pull to apply." },
    { n: "02", title: "Get Approved", desc: "Decision in under 4 hours on complete files." },
    { n: "03", title: "Get Funded", desc: "Wire hits your account within 24 hours of signing." },
];

const industries = [
    { icon: Stethoscope, label: "Healthcare" },
    { icon: Tool, label: "Trade" },
    { icon: Wrench, label: "Service" },
    { icon: UtensilsCrossed, label: "Food & Beverage" },
    { icon: ShoppingBag, label: "Retail" },
    { icon: Sun, label: "Seasonal" },
];

const testimonials = [
    {
        quote: "Bank turned us down twice. Lended had $185K in our account 22 hours after we sent the application. We hired three drivers the next week and didn't miss a delivery window all summer.",
        name: "Marcus T.",
        role: "Owner",
        firm: "Hudson Valley Logistics",
    },
    {
        quote: "Walk-in cooler died on a Friday. By Monday morning we had a $42K wire and a new unit installed. The restaurant never closed. That's the only reason we're still here.",
        name: "Daniela R.",
        role: "Owner",
        firm: "Casa Mariana Restaurant",
    },
    {
        quote: "I've worked with three other funders. Lended is the only one that actually picked up the phone. They restructured my payment when COVID hit and we made it through. I send everyone I know to them.",
        name: "James W.",
        role: "Founder",
        firm: "Highline Auto Repair",
    },
];

export default function Home() {
    usePageTitle(null, "Lended — Direct funder for small businesses. Up to $3M, decisions in 24 hours. Working capital, MCA, term loans, lines of credit. Fast. Reliable. Results.");
    return (
        <>
            {/* HERO */}
            <section className="relative bg-white overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0 bg-grid pointer-events-none" />
                <div className="absolute -top-20 -right-32 w-[520px] h-[520px] bg-[#FF6B1A]/10 blur-3xl rounded-full pointer-events-none" />
                <div className="container-lended relative pt-20 pb-24 md:pt-28 md:pb-32 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="divider-orange" />
                            <span className="eyebrow">Lended · Direct Funder</span>
                        </div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-[-0.04em] leading-[0.95]">
                            The capital your business needs.{" "}
                            <span className="text-[#FF6B1A]">Funded in 24 hours.</span>
                        </h1>
                        <p className="mt-7 text-lg md:text-xl text-black/70 max-w-2xl leading-relaxed">
                            From $10,000 to <strong className="text-black">$3 million</strong> — working capital,
                            merchant cash advances, lines of credit, and term loans for U.S. small businesses. No collateral on
                            most programs. Decisions in 4 hours. Funds wired the next business day.
                        </p>

                        <div className="mt-9 flex flex-wrap gap-4">
                            <a href="#apply" className="btn-orange" data-testid="hero-apply-cta">
                                Get My Funding Quote <ArrowRight size={18} />
                            </a>
                            <Link to="/contact" className="btn-outline-black" data-testid="hero-contact-cta">
                                Talk to a Funder
                            </Link>
                        </div>

                        {/* Three merchant pillars */}
                        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6">
                            {merchantPerks.map((p) => (
                                <div key={p.title} className="border-l-4 border-[#FF6B1A] pl-5" data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, '-')}`}>
                                    <div className="flex items-center gap-2 text-[#FF6B1A]">
                                        <p.icon size={18} />
                                        <span className="font-display font-bold text-lg uppercase tracking-tight">{p.title}.</span>
                                    </div>
                                    <p className="text-sm text-black/65 mt-1.5">{p.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5" id="apply">
                        <LeadForm />
                    </div>
                </div>
            </section>

            {/* STATS BAR */}
            <section className="bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="stats-section">
                <div className="absolute inset-0 bg-grid-dark opacity-50 pointer-events-none" />
                <div className="container-lended relative py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
                    {stats.map((s) => (
                        <div key={s.label}>
                            <div className="stat-num text-4xl md:text-6xl text-[#FF6B1A]">{s.value}</div>
                            <div className="text-xs md:text-sm uppercase tracking-[0.18em] mt-3 text-white/60">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS — 3 steps */}
            <section className="bg-white py-20 md:py-28" data-testid="how-strip">
                <div className="container-lended">
                    <div className="max-w-2xl mb-14">
                        <span className="eyebrow">How It Works</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1]">
                            Apply in 60 seconds. <span className="text-[#FF6B1A]">Funded in 24 hours.</span>
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {steps.map((s) => (
                            <div key={s.n} className="border-t-4 border-[#FF6B1A] pt-6" data-testid={`step-${s.n}`}>
                                <div className="font-display font-extrabold text-7xl text-[#FF6B1A]/15 leading-none">{s.n}</div>
                                <h3 className="font-display text-2xl font-bold mt-4">{s.title}</h3>
                                <p className="text-black/65 mt-2 leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY MERCHANTS CHOOSE US + FUNDING PRODUCTS */}
            <section className="bg-[#F8F5F2] py-20 md:py-28" data-testid="why-products-section">
                <div className="container-lended grid lg:grid-cols-2 gap-16">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <Diamond className="text-[#FF6B1A]" size={22} />
                            <span className="eyebrow">Why Businesses Choose Us</span>
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-[0.98] max-w-md">
                            Capital that respects how you actually run a business.
                        </h2>
                        <ul className="mt-10 space-y-7">
                            {whyMerchants.map((w) => (
                                <li key={w.title} className="flex gap-4 border-b border-black/15 pb-7 last:border-b-0">
                                    <div className="shrink-0 w-9 h-9 border-2 border-[#FF6B1A] flex items-center justify-center bg-white">
                                        <Check className="text-[#FF6B1A]" size={18} />
                                    </div>
                                    <div>
                                        <div className="font-display text-lg font-bold">{w.title}</div>
                                        <p className="text-black/65 mt-1">{w.desc}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <TrendingUp className="text-[#FF6B1A]" size={22} />
                            <span className="eyebrow">Funding Programs</span>
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-[0.98]">
                            Six programs. <br /> One direct funder. <br /> Pick what fits.
                        </h2>
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4" id="programs">
                            {products.map((p) => (
                                <div key={p.title} className="bg-white p-6 hover:shadow-[6px_6px_0_0_#FF6B1A] hover:-translate-y-0.5 transition-all border border-black/5" data-testid={`product-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                    <p.icon className="text-[#FF6B1A]" size={28} />
                                    <div className="font-display font-bold text-lg mt-4">{p.title}</div>
                                    <div className="text-xs font-mono text-[#FF6B1A] mt-1">{p.amount}</div>
                                    <p className="text-sm text-black/60 mt-2 leading-relaxed">{p.desc}</p>
                                </div>
                            ))}
                        </div>
                        <Link to="/funding" className="inline-flex items-center gap-2 mt-8 font-bold text-black hover:text-[#FF6B1A] text-sm" data-testid="see-all-programs">
                            See all funding programs <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* QUALIFY STRIP */}
            <section className="bg-white py-20" data-testid="qualify-section">
                <div className="container-lended grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <img
                            src="https://images.pexels.com/photos/4473496/pexels-photo-4473496.jpeg"
                            alt="Small business owner"
                            className="w-full h-[420px] object-cover grayscale hover:grayscale-0 transition-all duration-700"
                        />
                    </div>
                    <div className="lg:col-span-7">
                        <Clock className="text-[#FF6B1A]" size={32} />
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-4 leading-[0.98]">
                            Most businesses qualify <br /> in <span className="text-[#FF6B1A]">under 4 minutes.</span>
                        </h2>
                        <p className="text-black/70 text-lg mt-6 max-w-xl">
                            If you've been operating six months or more and doing $50K+ in monthly revenue, there's a strong
                            chance we have a program for you. No hard credit pull to apply. No commitment.
                        </p>
                        <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
                            {[
                                ["6+ mo", "in business"],
                                ["$50K+", "monthly revenue"],
                                ["U.S.-based", "operations"],
                                ["Business", "bank account"],
                            ].map(([v, l]) => (
                                <div key={l} className="border-l-2 border-[#FF6B1A] pl-3">
                                    <div className="font-display font-bold text-2xl">{v}</div>
                                    <div className="text-xs uppercase tracking-wider text-black/55 mt-0.5">{l}</div>
                                </div>
                            ))}
                        </div>
                        <a href="#apply" className="btn-black mt-8" data-testid="qualify-cta">
                            Check Eligibility <ArrowRight size={18} />
                        </a>
                    </div>
                </div>
            </section>

            {/* INDUSTRIES SERVED */}
            <section className="bg-white py-20 md:py-24 border-t border-black/10" data-testid="industries-section">
                <div className="container-lended">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="eyebrow">Industries We Fund</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1]">
                            Built for the businesses that built this country.
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-black/10">
                        {industries.map((i) => (
                            <div key={i.label} className="bg-white aspect-square flex flex-col items-center justify-center gap-3 hover:bg-[#0A0A0A] hover:text-white group transition-colors" data-testid={`industry-${i.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                <i.icon className="text-[#FF6B1A] group-hover:text-[#FF6B1A]" size={32} />
                                <div className="font-display font-bold uppercase tracking-tight text-sm">{i.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="bg-[#0A0A0A] text-white py-20 md:py-28 relative overflow-hidden" data-testid="testimonials-section">
                <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
                <div className="container-lended relative">
                    <div className="max-w-3xl mb-16">
                        <span className="eyebrow text-[#FF6B1A]">From Real Business Owners</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1]">
                            We measure ourselves on one thing: <span className="text-[#FF6B1A]">deals closed.</span>
                        </h2>
                        <div className="flex items-center gap-2 mt-6 text-[#FF6B1A]">
                            {[1,2,3,4,5].map((i) => <Star key={i} size={18} fill="currentColor" />)}
                            <span className="text-white/65 text-sm ml-2">4.9 / 5 from 1,200+ funded businesses</span>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-px bg-white/10">
                        {testimonials.map((t, i) => (
                            <div key={i} className="bg-[#0A0A0A] p-8 md:p-10 flex flex-col" data-testid={`testimonial-${i}`}>
                                <div className="font-display text-7xl text-[#FF6B1A] leading-none mb-4">"</div>
                                <p className="text-white/85 leading-relaxed flex-1">{t.quote}</p>
                                <div className="mt-8 pt-6 border-t border-white/15">
                                    <div className="font-display font-bold">{t.name}</div>
                                    <div className="text-sm text-white/50">{t.role} · {t.firm}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ISO PARTNERS QUIET LINK */}
            <section className="bg-[#F8F5F2] py-12 border-y border-black/10" data-testid="iso-quiet-banner">
                <div className="container-lended flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Briefcase className="text-[#FF6B1A]" size={24} />
                        <p className="text-black/75">
                            <strong>ISO or broker?</strong> We pay 14 points and fund deals up to $3M.
                        </p>
                    </div>
                    <Link to="/iso-partners" className="btn-outline-black text-sm" data-testid="iso-link">
                        ISO Partner Program <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-white py-20 md:py-28" data-testid="final-cta-section">
                <div className="container-lended">
                    <div className="bg-[#F8F5F2] border-2 border-[#0A0A0A] p-10 md:p-16 grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <Target className="text-[#FF6B1A]" size={36} />
                            <h2 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight mt-3 leading-[0.95]">
                                Ready to grow? <br /> <span className="text-[#FF6B1A]">Let's get you funded.</span>
                            </h2>
                            <p className="text-black/70 text-lg mt-4">No fees to apply. No hard credit pull. 60-second form.</p>
                        </div>
                        <div className="lg:col-span-5 flex flex-col gap-3">
                            <a href="#apply" className="btn-orange text-base justify-center" data-testid="final-apply-cta">
                                Get My Funding Quote <ArrowRight size={18} />
                            </a>
                            <Link to="/contact" className="btn-outline-black text-base justify-center">
                                Contact Us
                            </Link>
                            <a href="mailto:submissions@lended.com" className="text-sm text-black/65 hover:text-[#FF6B1A] mt-1 text-center">
                                or email submissions@lended.com
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
